// ============================================================
// Chess Arena — Match Timer
// ============================================================
// Configurable countdown timer per level.
// Emits 'timer_expired' when it reaches zero.
// Flashes red when < 10 seconds remaining.
// ============================================================

import eventBus from '../core/eventBus';

export class MatchTimer {
  private scene: Phaser.Scene;
  private totalSeconds = 0;
  private remaining = 0; // seconds remaining
  private paused = true;
  private active = false;
  private expired = false;

  // Visual
  private timerText: Phaser.GameObjects.Text | null = null;
  private flashTween: Phaser.Tweens.Tween | null = null;
  private isFlashing = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  /**
   * Initialise the timer for a level.
   * Pass `null` or `0` to disable the timer for this level.
   */
  init(seconds: number | null): void {
    this.expired = false;
    this.isFlashing = false;

    if (seconds == null || seconds <= 0) {
      this.active = false;
      this.remaining = 0;
      this.totalSeconds = 0;
      this.destroyVisuals();
      return;
    }

    this.totalSeconds = seconds;
    this.remaining = seconds;
    this.paused = true; // caller should resume() when gameplay starts
    this.active = true;
  }

  reset(): void {
    this.active = false;
    this.paused = true;
    this.expired = false;
    this.remaining = 0;
    this.isFlashing = false;
    this.destroyVisuals();
  }

  // ------------------------------------------------------------------
  // Frame update
  // ------------------------------------------------------------------

  update(delta: number): void {
    if (!this.active || this.paused || this.expired) return;

    const dt = delta / 1000;
    this.remaining -= dt;

    if (this.remaining <= 0) {
      this.remaining = 0;
      this.expired = true;
      this.onExpired();
      return;
    }

    // Flash red when < 10 seconds
    if (this.remaining < 10 && !this.isFlashing) {
      this.startFlash();
    }

    this.updateVisual();
  }

  // ------------------------------------------------------------------
  // Pause / Resume
  // ------------------------------------------------------------------

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    if (this.active && !this.expired) {
      this.paused = false;
    }
  }

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  getTimeRemaining(): number {
    return Math.max(this.remaining, 0);
  }

  isPaused(): boolean {
    return this.paused;
  }

  isActive(): boolean {
    return this.active;
  }

  hasExpired(): boolean {
    return this.expired;
  }

  /** 0-1 fraction of time elapsed (useful for progress bars). */
  getProgress(): number {
    if (this.totalSeconds <= 0) return 0;
    return 1 - this.remaining / this.totalSeconds;
  }

  // ------------------------------------------------------------------
  // Visuals (optional — can be managed externally via queries)
  // ------------------------------------------------------------------

  /**
   * Create an on-screen timer text.
   * Call this after init() if you want the built-in display.
   */
  createDisplay(x: number, y: number, style?: Phaser.Types.GameObjects.Text.TextStyle): void {
    this.destroyVisuals();

    if (!this.active) return;

    const defaultStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'Luckiest Guy, Fredoka, Arial, sans-serif',
      fontSize: '24px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      ...style,
    };

    this.timerText = this.scene.add.text(x, y, this.formatTime(), defaultStyle);
    this.timerText.setOrigin(0.5, 0.5);
    this.timerText.setDepth(100);
  }

  private updateVisual(): void {
    if (!this.timerText) return;
    this.timerText.setText(this.formatTime());
  }

  private formatTime(): string {
    const secs = Math.ceil(this.remaining);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (m > 0) {
      return `${m}:${s.toString().padStart(2, '0')}`;
    }
    return `${s}`;
  }

  // ------------------------------------------------------------------
  // Flash effect (< 10 seconds)
  // ------------------------------------------------------------------

  private startFlash(): void {
    this.isFlashing = true;

    if (!this.timerText) return;

    this.flashTween = this.scene.tweens.add({
      targets: this.timerText,
      alpha: { from: 1, to: 0.3 },
      duration: 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        if (this.timerText) {
          this.timerText.setColor('#ff3333');
        }
      },
    });
  }

  private stopFlash(): void {
    if (this.flashTween) {
      this.flashTween.destroy();
      this.flashTween = null;
    }
    this.isFlashing = false;
    if (this.timerText) {
      this.timerText.setAlpha(1);
      this.timerText.setColor('#ffffff');
    }
  }

  // ------------------------------------------------------------------
  // Expiry
  // ------------------------------------------------------------------

  private onExpired(): void {
    this.stopFlash();

    if (this.timerText) {
      this.timerText.setText('0');
      this.timerText.setColor('#ff0000');
      this.timerText.setAlpha(1);
    }

    eventBus.emit('timer_expired', {});
  }

  // ------------------------------------------------------------------
  // Cleanup
  // ------------------------------------------------------------------

  private destroyVisuals(): void {
    this.stopFlash();
    if (this.timerText) {
      this.timerText.destroy();
      this.timerText = null;
    }
  }

  destroy(): void {
    this.destroyVisuals();
    this.active = false;
  }
}
